import React, { useMemo } from 'react';
import { DefaultCustomerData, DesignDoc } from '../../../types/api-types';
import ArtDetailsContent from './artDetails/artDetailsContent';
import { useArtDetails } from './artDetails/useArtDetails';
import { mediaMap } from '../../../utils/artDetailsUtils';

// Styles
import styles from './artDetails.modal.module.css';

// Defining the prop types
interface ArtDetailsModalsProps {
  isOpen: boolean;
  onClose: () => void;
  document?: DesignDoc;
  defaultCustomerData: DefaultCustomerData;
}

function CloseIcon() {
  return (
    <button type="button" className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
      <span className="sr-only">Close menu</span>
      <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

const ArtDetailsModal: React.FC<ArtDetailsModalsProps> = ({ isOpen, onClose, document, defaultCustomerData }) => {
  const isEasyView = document?.wizardID === "EasyView";
  const isLNTransfer = document?.docType === "LNTransfer";

  const wizardDocData = useMemo(() => {
    let data = document?.wizardData;
    data = Array.isArray(data) ? data[0] : data;
    return isEasyView ? { ...data, ...data?.extendedData } : data;
  }, [document?.wizardData, isEasyView]);

  const wizardProps = useMemo(() => {
    if (!wizardDocData) return null;

    return Object.assign(
      wizardDocData.media ? { mediaText: mediaMap[wizardDocData.media as keyof typeof mediaMap] ?? "" } : {},
      wizardDocData
    );
  }, [wizardDocData]);

  const { labelMap, labelKeys, mappedCards, unmappedKeys } = useArtDetails(wizardDocData, wizardProps, isEasyView, isLNTransfer, document?.id as string | null);

  const contactData = useMemo(() => {
    const contactInfo = {
      document: {
        notificationsEmail: !document?.notificationsEmail && defaultCustomerData?.email
          ? `${defaultCustomerData?.email} (default)`
          : document?.notificationsEmail,
        contactName: !document?.contactName && defaultCustomerData?.fullName
          ? `${defaultCustomerData?.fullName} (default)`
          : document?.contactName,
        contactPhone: !document?.contactPhone && defaultCustomerData?.phone
          ? `${defaultCustomerData?.phone} (default)`
          : document?.contactPhone
      }
    };

    return contactInfo;
  }, [document, defaultCustomerData, wizardProps]);

  return isOpen ? (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modalContainer} relative w-auto my-auto mx-auto max-w-3xl`}>
        <div className={`${styles.modalWrapper} border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none`}>
          <div className={styles.header}>
            <span>Art Details</span>
            <div className={styles.closeIcon} onClick={() => { onClose(); }}>
              <CloseIcon />
            </div>
          </div>

          <div className={styles.modalContent}>
            <ArtDetailsContent
              labelMap={labelMap ?? new Map()}
              mappedCards={mappedCards}
              unmappedKeys={unmappedKeys}
              labelKeys={labelKeys}
              wizardProps={wizardProps}
              viewMoreDetails={true}
              contactData={contactData}
            />
          </div>

          <div className={`${styles.footerButtons} p-6 border-t border-solid border-blueGray-200 rounded-b`}>
            <button className={[styles.button, styles.cancelBtn].join(' ')} onClick={() => { onClose(); }}>Close</button>
          </div>
        </div>
      </div>
    </div >
  ) : null;
};

export default ArtDetailsModal;